// Custom store. The main get route fetches data from a custom route.
import {
  IBuildings, IBuildingsHasPhotos, IListingFloorplans, IListingPhotos, IVideos, IVirtualTours,
} from '@llws/lift-entity-interfaces';
import { ActionContext } from 'vuex';
import { cloneObject, IApiResponse, subsetObject } from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { IFileUploadSettings } from '@/shared/interfaces/file-upload-settings';

export interface IPropertyMediaStoreState {
  buildingsHasPhotos: IBuildingsHasPhotos[];
  listingPhotos: IListingPhotos[];
  listingFloorplans: IListingFloorplans[];
  videos: IVideos[];
  virtualTours: IVirtualTours[];
}

export interface IPropertyMediaStoreStateWithStateCopy extends IPropertyMediaStoreState {
  initialMedia?: IPropertyMediaStoreState | null;
  photosToUpload?: IFileUploadSettings[] | null;
}

const initialState: IPropertyMediaStoreStateWithStateCopy = {
  buildingsHasPhotos: [],
  listingPhotos: [],
  listingFloorplans: [],
  videos: [],
  virtualTours: [],
  initialMedia: null,
  photosToUpload: null,
};

export default {
  namespaced: true, // 'property-media',

  state: initialState,

  getters: {
    media: (state: IPropertyMediaStoreStateWithStateCopy) => subsetObject(state, ['stateCopy']) as IPropertyMediaStoreState,

    initialMedia: (state: IPropertyMediaStoreStateWithStateCopy) => state.initialMedia,

    currentFeaturedPhoto: (state: IPropertyMediaStoreStateWithStateCopy) => state.listingPhotos.find((v) => v.searchResult),

    oldFeaturedPhoto: (state: IPropertyMediaStoreStateWithStateCopy) => state.initialMedia?.listingPhotos.find((v) => v.searchResult),

    photosToUpload: (state: IPropertyMediaStoreStateWithStateCopy) => state.photosToUpload || [],

    areBuildingsHasPhotosChanged: (state: IPropertyMediaStoreStateWithStateCopy): boolean => (
      state.initialMedia?.buildingsHasPhotos.length !== state.buildingsHasPhotos.length
      || state.initialMedia?.buildingsHasPhotos.some((v) => v.orderBy !== state.buildingsHasPhotos.find((bhp) => bhp.id === v.id)?.orderBy)
    ),
    areFloorPlansChanged: (state: IPropertyMediaStoreStateWithStateCopy): boolean => (
      JSON.stringify(state.initialMedia?.listingFloorplans) !== JSON.stringify(state.listingFloorplans)
    ),
    areVideosChanged: (state: IPropertyMediaStoreStateWithStateCopy): boolean => (
      JSON.stringify(state.initialMedia?.videos) !== JSON.stringify(state.videos)
    ),
    areVirtualToursChanged: (state: IPropertyMediaStoreStateWithStateCopy): boolean => (
      JSON.stringify(state.initialMedia?.virtualTours) !== JSON.stringify(state.virtualTours)
    ),
  },

  mutations: {
    set: (state: IPropertyMediaStoreStateWithStateCopy, payload: IPropertyMediaStoreState) => {
      state.buildingsHasPhotos = payload.buildingsHasPhotos;
      state.listingPhotos = payload.listingPhotos;
      state.listingFloorplans = payload.listingFloorplans;
      state.videos = payload.videos;
      state.virtualTours = payload.virtualTours;
    },

    setByKey: (
      state: IPropertyMediaStoreStateWithStateCopy,
      payload: {
        key: 'buildingsHasPhotos' | 'listingPhotos' | 'listingFloorplans' | 'videos' | 'virtualTours' | 'initialMedia',
        data: IBuildingsHasPhotos[] | IListingFloorplans[] | IListingPhotos[] | IVideos[] | IVirtualTours[] | IPropertyMediaStoreState,
        updateInitialMedia?: boolean
      },
    ) => {
      state[payload.key] = payload.data as any; // TODO: Need to refactor this function to better handle types.
      if (payload.updateInitialMedia && state?.initialMedia && payload.key !== 'initialMedia') {
        state.initialMedia[payload.key] = payload.data as any;
      }
    },

    setPhotosToUpload: (
      state: IPropertyMediaStoreStateWithStateCopy,
      payload: IFileUploadSettings[],
    ) => {
      state.photosToUpload = payload;
    },
  },

  actions: {
    get: async (ctx: ActionContext<IPropertyMediaStoreState, any>, propertyId: number) => {
      const result: IApiResponse<IBuildings, null> = await adminRequest.get(`/property-api/properties/media/${propertyId}`);
      const mediaState = {
        buildingsHasPhotos: result.data?.buildingsHasPhotos!,
        listingPhotos: result.data?.listingPhotos!,
        listingFloorplans: result.data?.listingFloorplans!,
        videos: result.data?.videos!,
        virtualTours: result.data?.virtualTours!,
      } as IPropertyMediaStoreState;

      ctx.commit('set', mediaState);
      ctx.commit('setByKey', { key: 'initialMedia', data: cloneObject(mediaState) });
    },
  },
};
